import { collect, iterate } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { getCursor, putCursor, itemNeedsExport, putItem } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import {
    withinRange,
    isContainerSelected,
    walkScope,
    scopeCovers,
    type DateRange,
    type ContainerSelection
} from "../../export/plan.ts";
import { calendarKey, eventRawPath } from "./paths.ts";
import { renderCalendarFile, renderCalendarIndex, type CalendarEventGroup } from "./disk-render.ts";
import type { Calendar, CalendarEvent } from "./types.ts";

/**
 * Calendar export (ARCHITECTURE.md §15 step 8).
 *
 * Structurally close to Mail: one delta cursor per container (a calendar,
 * `cursors` subKey = calendar id), the same as Mail keys one per folder.
 *
 * Every request carries `Prefer: outlook.timezone="UTC"`, which asks Graph
 * to convert every start/end it returns to UTC before sending it — see
 * ics.ts for why this sidesteps needing a Windows-zone-name → IANA VTIMEZONE
 * mapping entirely.
 *
 * `/events/delta` returns series masters and modified/cancelled occurrences
 * (`type: "exception"`) as separate entries, not expanded individual
 * instances. A master becomes one RRULE-bearing VEVENT (ics.ts); an
 * exception becomes its own standalone VEVENT rather than a properly linked
 * RECURRENCE-ID override, because Graph's delta does not expose the
 * original (pre-modification) occurrence time an exception replaces — only
 * its new one. Linking on a guessed time would let a calendar client apply
 * the wrong replacement silently; a disclosed standalone duplicate is the
 * safer failure mode. A future pass through the `/instances` endpoint per
 * series is the way to resolve this properly — deferred, not designed away.
 */

const SCOPES = MODULE_SCOPES.calendar;
const UTC_HEADERS = { Prefer: 'outlook.timezone="UTC"' };

export interface CalendarProgress {
    calendarsTotal: number;
    calendarsDone: number;
    currentLabel?: string;
    currentBytes: number;
    totalBytes: number;
}

export type CalendarProgressCallback = (progress: CalendarProgress) => void;

export interface CalendarSummary {
    label: string;
    events: number;
    bytes: number;
}

export interface ExportCalendarParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** From the Scan step: only export these calendars. */
    selection?: ContainerSelection;
    /** From the Scan step: skip events whose start is outside this window. */
    dateRange?: DateRange;
    onProgress?: CalendarProgressCallback;
    onCalendarComplete?: (summary: CalendarSummary) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface CalendarResult {
    calendarsTotal: number;
    events: number;
    bytes: number;
    errors: number;
}

async function exportCalendarEvents(params: {
    runId: string;
    root: FileSystemDirectoryHandle;
    calendar: Calendar;
    key: string;
    dateRange?: DateRange;
    signal?: AbortSignal;
    onBytes?: (bytes: number) => void;
}): Promise<{ events: number; bytes: number }> {
    const { runId, root, calendar, key, dateRange, signal, onBytes } = params;

    const scope = walkScope(dateRange);
    const stored = await getCursor(runId, "calendar", calendar.id);
    // A cursor walked under a narrower date window moved past whatever that
    // window skipped; continuing from it would never list those items again,
    // so a wider window re-lists the container (done items are still skipped).
    const cursor = stored && scopeCovers(stored.scope, scope) ? stored : undefined;
    const startUrl =
        cursor?.deltaLink ?? cursor?.nextLink ?? `/me/calendars/${calendar.id}/events/delta`;

    let events = 0;
    let bytes = 0;

    for await (const event of iterate<CalendarEvent>(startUrl, {
        scopes: SCOPES,
        service: "outlook",
        headers: UTC_HEADERS,
        ...(signal ? { signal } : {}),
        onDeltaLink: (deltaLink) => {
            void putCursor({ runId, module: "calendar", subKey: calendar.id, deltaLink, done: true, scope });
        },
        onNextLink: (nextLink) => {
            void putCursor({ runId, module: "calendar", subKey: calendar.id, nextLink, done: false, scope });
        }
    })) {
        throwIfAborted(signal);

        if (event["@removed"]) {
            // Not fetched, and not deleted from disk either — same "backup,
            // not mirror" policy as Mail and OneDrive apply to their own
            // deletions.
            continue;
        }

        const itemId = event.id;
        // A rescheduled or edited event comes back through delta with a new
        // lastModifiedDateTime and its full new state, which replaces the
        // earlier raw file (and so the .ics rebuilt from those files).
        const version = event.lastModifiedDateTime ?? event.changeKey;
        if (!(await itemNeedsExport(runId, "calendar", itemId, version))) {
            continue;
        }

        // Date filter (Scan step) on the event's start.
        if (dateRange && !withinRange(event.start?.dateTime, dateRange)) {
            continue;
        }

        const written = await writeFile(root, eventRawPath(key, event.id), JSON.stringify(event));
        await putItem({
            runId,
            module: "calendar",
            itemId,
            status: "done",
            bytes: written,
            ...(version !== undefined ? { version } : {})
        });

        events += 1;
        bytes += written;
        onBytes?.(written);
    }

    return { events, bytes };
}

export async function exportCalendar(params: ExportCalendarParams): Promise<CalendarResult> {
    const { runId, root, selection, dateRange, onProgress, onCalendarComplete, signal, logger } = params;

    await logger.info("listing calendars");
    const allCalendars = await collect<Calendar>("/me/calendars", {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {})
    });
    const calendars = allCalendars.filter((c) => isContainerSelected(selection, c.id));
    await logger.info(`found ${allCalendars.length} calendars, ${calendars.length} selected`);

    let totalBytes = 0;
    let totalEvents = 0;
    let errors = 0;
    const groups: CalendarEventGroup[] = [];

    for (let i = 0; i < calendars.length; i += 1) {
        throwIfAborted(signal);

        const calendar = calendars[i];
        if (!calendar) {
            continue;
        }

        const label = calendar.name || "Calendar";
        let calendarBytes = 0;

        onProgress?.({
            calendarsTotal: calendars.length,
            calendarsDone: i,
            currentLabel: label,
            currentBytes: 0,
            totalBytes
        });

        const key = await calendarKey(calendar);

        try {
            const result = await exportCalendarEvents({
                runId,
                root,
                calendar,
                key,
                ...(dateRange ? { dateRange } : {}),
                ...(signal ? { signal } : {}),
                onBytes: (chunk) => {
                    calendarBytes += chunk;
                    totalBytes += chunk;
                    onProgress?.({
                        calendarsTotal: calendars.length,
                        calendarsDone: i,
                        currentLabel: label,
                        currentBytes: calendarBytes,
                        totalBytes
                    });
                }
            });

            totalEvents += result.events;
            onCalendarComplete?.({ label, events: result.events, bytes: result.bytes });
        } catch (err) {
            if (err instanceof ExportAbortedError) {
                throw err;
            }
            if (err instanceof InteractionRequiredError || err instanceof NotSignedInError) {
                throw err;
            }
            errors += 1;
            const message = err instanceof GraphError ? err.toLogString() : String(err);
            await logger.error(`calendar failed, will retry on resume: ${message}`);
            onCalendarComplete?.({ label, events: 0, bytes: 0 });
        }

        // Idempotent and network-free — safe whether this calendar just
        // finished, had nothing new, or failed above and is only partially
        // fetched so far.
        const events = await renderCalendarFile(root, key, label);
        groups.push({ key, calendarName: label, events });
    }

    // Idempotent and network-free, like each calendar's own .ics above.
    await renderCalendarIndex(root, groups);

    onProgress?.({
        calendarsTotal: calendars.length,
        calendarsDone: calendars.length,
        currentBytes: 0,
        totalBytes
    });
    await logger.info(`calendar export complete: ${totalEvents} events, ${errors} errors`);

    return { calendarsTotal: calendars.length, events: totalEvents, bytes: totalBytes, errors };
}
